import classNames from 'classnames'
import deepEqual from 'fast-deep-equal'
import plur from 'plur'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {useBuildId, useBuildTypeId, useQueryParamState} from '../../../../../hooks/routes'
import {useIsAutodetectTimeZone} from '../../../../../hooks/useIsAutodetectTimeZone'
import {getBuild, getBuildStatusTypeFromBuild} from '../../../../../selectors'
import {BuildStatusType} from '../../../../../types'
import {displayDate} from '../../../../../utils/dateTimeUtils'
import BuildBranch from '../../../../common/BranchLabel/BuildBranchLabel.container'
import BuildNumberContainer from '../../../../common/BuildNumber/BuildNumber.container'
import {formatBuildNumber} from '../../../../common/BuildNumber/BuildNumber.utils'
import BuildStatusLink from '../../../../common/BuildStatus/BuildStatusLink/BuildStatusLink.container'
import Details from '../../../../common/Details/Details'
import SvgIcon from '../../../../common/SvgIcon/SvgIcon'
import {getBuildTypeChainLocator} from '../../../BuildPage/DependenciesTab/DependenciesTab.utils'

import type {BuildTypeChainBlockQuery} from './__generated__/BuildTypeChainBlockQuery.graphql'
import BuildTypeChainContent from './BuildTypeChainContent/BuildTypeChainContent'

import styles from './BuildTypeChainBlock.css'

import {useAppSelector} from 'src/hooks/react-redux'

const statuses = [
  {key: 'failure', description: 'failed'},
  {key: 'failure_running', description: 'running failing'},
  {key: 'success', description: 'successful'},
  {key: 'success_running', description: 'running successfully'},
  {key: 'canceled', description: 'canceled'},
  {key: 'queued', description: 'queued'},
  {key: 'pending', description: 'pending'},
]

const PERCENTS = 100

const query = graphql`
  query BuildTypeChainBlockQuery($locator: String!) {
    buildTypes(locator: $locator) {
      buildType {
        rawId
      }
    }
  }
`

export default function BuildTypeChainBlock() {
  const buildId = useBuildId()!
  const stringifiedId = String(buildId)
  const [expandedId, setExpandedId] = useQueryParamState('expandChain')
  const expanded = stringifiedId === expandedId
  const buildTypeId = useBuildTypeId()!
  const {buildTypes} = useLazyLoadQuery<BuildTypeChainBlockQuery>(query, {
    locator: getBuildTypeChainLocator(buildTypeId),
  })
  const stats = useAppSelector(state => {
    const build = getBuild(state, buildId)
    const allBuilds = [
      build,
      ...(build?.related?.builds?.build?.map(id => getBuild(state, id)) ?? []),
    ]
    const counts: Record<string, number> = {}
    const unusedBuildTypes = new Set(buildTypes?.buildType?.map(buildType => buildType.rawId) ?? [])
    const usedBuildTypes = new Set()
    allBuilds.forEach(item => {
      const itemBuildTypeId = item?.buildType ?? null
      if (usedBuildTypes.has(itemBuildTypeId)) {
        return
      }
      usedBuildTypes.add(itemBuildTypeId)
      unusedBuildTypes.delete(itemBuildTypeId)
      const statusType = getBuildStatusTypeFromBuild(item)
      let key = statusType as string
      if (
        [BuildStatusType.SUCCESS, BuildStatusType.FAILURE].includes(statusType) &&
        item?.state === 'running'
      ) {
        key += '_running'
      }
      counts[key] ??= 0
      counts[key]++
    })
    if (unusedBuildTypes.size > 0) {
      counts.pending = unusedBuildTypes.size
    }
    const max = Math.max(...Object.values(counts))
    const nonEmptyStatuses = statuses.filter(({key}) => counts[key] != null)
    return nonEmptyStatuses.length > 1
      ? nonEmptyStatuses.map(status => ({
          ...status,
          count: counts[status.key],
          height: counts[status.key] / max,
        }))
      : []
  }, deepEqual)
  const buildNumber = useAppSelector(state => {
    const {number, state: buildState, queuePosition} = getBuild(state, buildId) ?? {}
    return formatBuildNumber({number, isQueued: buildState === 'queued', queuePosition}).text
  })
  const startDate = useAppSelector(state => getBuild(state, buildId)?.startDate)
  const triggeredDate = useAppSelector(state => getBuild(state, buildId)?.triggered?.date)
  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  return (
    <Details
      open={expanded}
      onToggle={newExpanded => setExpandedId(newExpanded ? stringifiedId : null)}
      label={buildNumber}
      headingClassName={styles.heading}
      summaryClassName={styles.summary}
      summary={
        <div className={styles.block}>
          <SvgIcon icon={expanded ? 'chevron-down' : 'chevron-right'} className={styles.chevron} />
          <div className={styles.chart}>
            {stats.map(({key, description, count, height}) => (
              <div
                key={key}
                className={styles.barContainer}
                title={`${count} ${description} ${plur('build', count)}`}
              >
                <div
                  className={classNames(styles.bar, {
                    [styles.barContainer]: key === 'container',
                    [styles.barFailure]: key === 'failure',
                    [styles.barFailureRunning]: key === 'failure_running',
                    [styles.barSuccess]: key === 'success',
                    [styles.barSuccessRunning]: key === 'success_running',
                    [styles.barCanceled]: key === 'canceled',
                    [styles.barQueued]: key === 'queued',
                    [styles.barPending]: key === 'pending',
                  })}
                  style={{height: `${height * PERCENTS}%`}}
                />
              </div>
            ))}
          </div>
          <BuildBranch noLink buildId={buildId} className={styles.branch} />
          <BuildNumberContainer
            withLink={false}
            buildId={buildId}
            hideStar
            className={styles.number}
          />
          <BuildStatusLink noLink buildId={buildId} />
          <div className={styles.started}>
            {startDate != null
              ? `Started ${displayDate(startDate, isAutodetectTimeZone, true)}`
              : `Triggered ${displayDate(triggeredDate!, isAutodetectTimeZone, true)}`}
          </div>
        </div>
      }
    >
      <BuildTypeChainContent />
    </Details>
  )
}
